import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserSlice } from '../../context/store/store';
import {
    getNotifications,
    markAllNotificationsAsRead,
    markNotificationAsRead
} from '../../helpers/axiosHelper';
import { timeFormatter } from '../../helpers/timeZoneHelper';
import {
    disablePushNotifications,
    enablePushNotifications,
    syncExistingPushNotifications
} from '../../helpers/pushNotifications';
import './ClientNotifications.css';

const POLL_INTERVAL_MS = 30000;

const BellIcon = () => (
    <svg aria-hidden="true" viewBox="0 0 24 24">
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" />
    </svg>
);

const validNotification = (notification) => (
    notification
    && typeof notification._id === 'string'
    && notification.audience === 'client'
    && typeof notification.type === 'string'
    && typeof notification.title === 'string'
    && typeof notification.message === 'string'
);

const notificationDestination = (type) => {
    if (['PENDING_ORDER_CREATED', 'PROFILES_ASSIGNED', 'PENDING_ORDER_COMPLETED'].includes(type)) {
        return '/movements';
    }
    if (type === 'RECHARGE_COMPLETED') return '/recharge/historial';
    return null;
};

const ClientNotifications = () => {
    const navigate = useNavigate();
    const { headers } = getUserSlice();
    const authorization = headers.Authorization;
    const [notifications, setNotifications] = useState([]);
    const [open, setOpen] = useState(false);
    const [featured, setFeatured] = useState(null);
    const [markingAll, setMarkingAll] = useState(false);
    const [pushState, setPushState] = useState('checking');
    const [changingPush, setChangingPush] = useState(false);
    const announcedRef = useRef(new Set());
    const requestRef = useRef(0);
    const pendingMutationsRef = useRef(0);
    const sessionRef = useRef(0);

    const applyFetchedNotifications = useCallback((response, requestId) => {
        if (
            requestId !== requestRef.current
            || pendingMutationsRef.current > 0
            || getUserSlice.getState().headers.Authorization !== authorization
        ) return;
        const rows = Array.isArray(response?.data) ? response.data.filter(validNotification) : [];
        setNotifications(rows);
        const completion = rows.find((row) => (
            ['PENDING_ORDER_COMPLETED', 'RECHARGE_COMPLETED'].includes(row.type)
            && !row.readAt
            && !announcedRef.current.has(row._id)
        ));
        if (completion) {
            announcedRef.current.add(completion._id);
            setFeatured(completion);
        }
    }, [authorization]);

    const load = useCallback(async () => {
        if (!authorization) return;
        const requestId = ++requestRef.current;
        try {
            const response = await getNotifications({ headers });
            applyFetchedNotifications(response, requestId);
        } catch (error) {
            // Notifications are complementary: a temporary polling failure
            // must never interrupt shopping or expose transport details.
        }
    }, [authorization, headers, applyFetchedNotifications]);

    // Used only to reconcile after a mark-as-read mutation settles. Unlike
    // load()'s single silent attempt (fine for background polling), the
    // fetch right after a user action gets one immediate retry: if the
    // write itself also failed, this is what the user is looking at right
    // now, so a lone transient blip on this follow-up read shouldn't leave
    // the UI on a stale optimistic state until the next poll/focus.
    const reconcile = useCallback(async (mutationSession) => {
        if (!authorization || sessionRef.current !== mutationSession) return;
        try {
            const requestId = ++requestRef.current;
            const response = await getNotifications({ headers });
            applyFetchedNotifications(response, requestId);
            return;
        } catch (error) {
            // Retried once below.
        }
        if (sessionRef.current !== mutationSession) return;
        try {
            const retryId = ++requestRef.current;
            const response = await getNotifications({ headers });
            applyFetchedNotifications(response, retryId);
        } catch (error) {
            // Falls back to the next poll/focus, same as load().
        }
    }, [authorization, headers, applyFetchedNotifications]);

    useEffect(() => {
        requestRef.current += 1;
        // A write left pending by the previous session (e.g. a hung request
        // with no configured timeout) must not block this session's fetches
        // forever, nor corrupt this session's count if it resolves later —
        // bumping the generation makes its finally block a no-op.
        sessionRef.current += 1;
        pendingMutationsRef.current = 0;
        announcedRef.current = new Set();
        setNotifications([]);
        setFeatured(null);
        setMarkingAll(false);
        setOpen(false);
        if (!authorization) return undefined;

        load();
        const interval = window.setInterval(() => {
            if (document.visibilityState === 'visible') load();
        }, POLL_INTERVAL_MS);
        const onFocus = () => load();
        window.addEventListener('focus', onFocus);
        return () => {
            window.clearInterval(interval);
            window.removeEventListener('focus', onFocus);
            requestRef.current += 1;
        };
    }, [authorization, load]);

    useEffect(() => {
        let active = true;
        if (!authorization) {
            setPushState('checking');
            return () => { active = false; };
        }
        setPushState('checking');
        syncExistingPushNotifications({ headers })
            .then((state) => { if (active) setPushState(state); })
            .catch(() => { if (active) setPushState('error'); });
        return () => { active = false; };
    }, [authorization, headers]);

    useEffect(() => {
        const serviceWorker = window.navigator?.serviceWorker;
        if (!serviceWorker?.addEventListener) return undefined;
        const onWorkerMessage = (event) => {
            const path = event?.data?.type === 'GOLD_NOTIFICATION_NAVIGATE'
                ? event.data.path
                : null;
            if (!['/movements', '/recharge/historial'].includes(path)) return;
            setOpen(false);
            setFeatured(null);
            navigate(path);
        };
        serviceWorker.addEventListener('message', onWorkerMessage);
        return () => serviceWorker.removeEventListener('message', onWorkerMessage);
    }, [navigate]);

    const unread = useMemo(() => notifications.filter((notification) => !notification.readAt).length, [notifications]);

    const readAll = async () => {
        if (!unread || markingAll) return;
        const mutationSession = sessionRef.current;
        requestRef.current += 1;
        pendingMutationsRef.current += 1;
        const readAt = new Date().toISOString();
        setMarkingAll(true);
        setNotifications((current) => current.map((row) => (
            row.readAt ? row : { ...row, readAt }
        )));
        setFeatured(null);
        try {
            await markAllNotificationsAsRead({ headers });
        } catch (error) {
            // A stale or failed write is reconciled below, once no other
            // mutation is left pending, by re-fetching the server's truth.
        } finally {
            // A session change already reset the shared counter for the new
            // session; a write from a session that no longer applies must
            // not touch it — or that session's own markingAll flag — once
            // it finally settles.
            if (sessionRef.current === mutationSession) {
                setMarkingAll(false);
                pendingMutationsRef.current -= 1;
                if (pendingMutationsRef.current === 0) await reconcile(mutationSession);
            }
        }
    };

    const read = async (notification, destination = null) => {
        const mutationSession = sessionRef.current;
        requestRef.current += 1;
        pendingMutationsRef.current += 1;
        setNotifications((current) => current.map((row) => (
            row._id === notification._id ? { ...row, readAt: row.readAt || new Date().toISOString() } : row
        )));
        if (featured?._id === notification._id) setFeatured(null);
        if (destination) {
            setOpen(false);
            navigate(destination, { state: { notificationId: notification._id } });
        }
        try {
            await markNotificationAsRead({ id: notification._id, headers });
        } catch (error) {
            // Reconciled below, same as readAll.
        } finally {
            if (sessionRef.current === mutationSession) {
                pendingMutationsRef.current -= 1;
                if (pendingMutationsRef.current === 0) await reconcile(mutationSession);
            }
        }
    };

    const changePush = async () => {
        if (changingPush) return;
        setChangingPush(true);
        try {
            const state = pushState === 'enabled'
                ? await disablePushNotifications({ headers })
                : await enablePushNotifications({ headers });
            setPushState(state);
        } catch (error) {
            setPushState('error');
        } finally {
            setChangingPush(false);
        }
    };

    const pushMessage = {
        checking: 'Comprobando este dispositivo…',
        enabled: 'Recibirás avisos incluso cuando Servicio Gold no esté abierto.',
        available: 'Actívalos para saber cuando tu pedido o recarga tenga novedades.',
        denied: 'El navegador bloqueó los avisos. Puedes habilitarlos desde la configuración del sitio.',
        'ios-install-required': 'En iPhone o iPad, agrega Servicio Gold a la pantalla de inicio para activar los avisos.',
        unsupported: 'Este navegador no admite notificaciones push.',
        unavailable: 'Las notificaciones push no están disponibles temporalmente.',
        error: 'No pudimos configurar los avisos en este dispositivo. Intenta nuevamente.'
    }[pushState] || 'Configura los avisos de este dispositivo.';

    if (!authorization) return null;

    return (
        <aside className="clientNotifications" aria-label="Notificaciones de tu cuenta">
            {featured && (
                <div className="clientNotificationToast" role="status">
                    <span className="clientNotificationToastLabel">
                        {featured.type === 'RECHARGE_COMPLETED' ? 'Saldo actualizado' : 'Entrega completada'}
                    </span>
                    <strong>{featured.title}</strong>
                    <p>{featured.message}</p>
                    <div>
                        <button type="button" onClick={() => setFeatured(null)}>Después</button>
                        <button
                            type="button"
                            onClick={() => read(featured, notificationDestination(featured.type))}
                        >
                            {featured.type === 'RECHARGE_COMPLETED' ? 'Ver recarga' : 'Ver credenciales'}
                        </button>
                    </div>
                </div>
            )}

            {open && (
                <div className="clientNotificationsPanel">
                    <div className="clientNotificationsHeader">
                        <div>
                            <span>Actividad reciente</span>
                            <strong>Notificaciones</strong>
                        </div>
                        <button type="button" aria-label="Cerrar notificaciones" onClick={() => setOpen(false)}>×</button>
                    </div>
                    <section className={`clientPushPreference ${pushState === 'enabled' ? 'enabled' : ''}`}>
                        <div>
                            <strong>{pushState === 'enabled' ? 'Avisos push activos' : 'Avisos en este dispositivo'}</strong>
                            <span>{pushMessage}</span>
                        </div>
                        {['available', 'enabled', 'error'].includes(pushState) && (
                            <button type="button" disabled={changingPush} onClick={changePush}>
                                {changingPush
                                    ? 'Procesando…'
                                    : pushState === 'enabled' ? 'Desactivar' : 'Activar'}
                            </button>
                        )}
                    </section>
                    {notifications.length > 0 && (
                        <div className="clientNotificationsToolbar">
                            <span aria-live="polite">{unread ? `${unread} sin leer` : 'Todo al día'}</span>
                            <button type="button" disabled={!unread || markingAll} onClick={readAll}>
                                {markingAll ? 'Marcando…' : unread ? 'Marcar todas como leídas' : 'Todas leídas'}
                            </button>
                        </div>
                    )}
                    <div className="clientNotificationsList">
                        {notifications.length === 0
                            ? <p className="clientNotificationsEmpty">No tienes novedades pendientes.</p>
                            : notifications.slice(0, 12).map((notification) => (
                                <button
                                    type="button"
                                    className={notification.readAt ? 'read' : 'unread'}
                                    key={notification._id}
                                    onClick={() => read(notification, notificationDestination(notification.type))}
                                >
                                    <span>{notification.title}</span>
                                    <strong>{notification.message}</strong>
                                    <small>{notification.createdAt ? timeFormatter(notification.createdAt) : 'Reciente'}</small>
                                </button>
                            ))}
                    </div>
                </div>
            )}

            <button
                type="button"
                className="clientNotificationsTrigger"
                aria-label={unread ? `Notificaciones, ${unread} sin leer` : 'Notificaciones'}
                aria-expanded={open}
                onClick={() => setOpen((current) => !current)}
            >
                <BellIcon />
                {unread > 0 && <span>{unread > 9 ? '9+' : unread}</span>}
            </button>
        </aside>
    );
};

export default ClientNotifications;
